import type { PartialFlagValues } from './definitions';

import { pickFlagValues } from './values';

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

export type OverridesOptions = {
  storageKey?: string;
  /** Defaults to localStorage. */
  storage?: StorageLike;
};

function getLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    // Access throws when site data is blocked.
    return undefined;
  }
}

function readStored<Name extends string>(
  names: readonly Name[],
  storage: StorageLike | undefined,
  key: string
): PartialFlagValues<Name> {
  try {
    const raw = storage?.getItem(key);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object'
      ? pickFlagValues(names, parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

function writeStored(
  storage: StorageLike | undefined,
  key: string,
  values: Readonly<Record<string, boolean | undefined>>
) {
  try {
    if (Object.keys(values).length === 0) {
      storage?.removeItem(key);
    } else {
      storage?.setItem(key, JSON.stringify(values));
    }
  } catch {
    // Overrides still apply to the current page load.
  }
}

export type Overrides<Name extends string> = {
  values: PartialFlagValues<Name>;
  save: (values: PartialFlagValues<Name>) => void;
};

/** Manual overrides for development and QA, set in devtools and persisted in storage. */
export function loadOverrides<Name extends string>(
  names: readonly Name[],
  {
    storageKey = 'feature-flags',
    storage = getLocalStorage(),
  }: OverridesOptions
): Overrides<Name> {
  return {
    values: readStored(names, storage, storageKey),
    save: values => {
      writeStored(storage, storageKey, values);
    },
  };
}
